<!-- eslint-disable vue/no-v-html -->
<template>
    <div class="sticky top-0">
        <ProductDetailStructuredData />

        <div>
            <section class="relative mb-8 flex gap-3 justify-between flex-col md:gap-0">
                <ProductManufacturer />

                <div>
                    <h1 class="font-semibold text-xl">
                        {{ product.name }}
                    </h1>

                    <LazyProductDetailGender
                        v-if="product.customFields?.gender"
                        :gender="product.customFields.gender"
                    />

                    <span class="text-sm text-base-content-subtle">
                        {{ $t('product.detail.buyBox.productNumber') }} {{ product.productNumber }}
                    </span>

                    <div
                        v-if="product.ratingAverage != null"
                        class="flex items-center gap-1 cursor-pointer"
                        role="button"
                        tabindex="0"
                        @click="showReviews()"
                        @keyup.enter="showReviews()"
                    >
                        <div class="min-w-[120px] min-h-[24px]">
                            <MiscStars
                                v-if="product.ratingAverage != null"
                                :percentage="100 / 5 * product.ratingAverage"
                                :width="120"
                                :height="24"
                            />
                        </div>
                        <span class="sr-only">{{ $t('product.detail.reviews.formPointsLabel')}}</span>
                        <span class="text-sm text-base-content-subtle pl-1">{{ round(product.ratingAverage, 2) }}</span>
                    </div>
                </div>
            </section>

            <section class="mb-6">
                <ProductPrice
                    :all-options-selected="allOptionsSelected"
                    :is-dynamic-buyable-voucher="isDynamicBuyableVoucher"
                />
            </section>

            <section
                v-if="shouldShowDeliveryTime"
                class="flex items-center gap-2 mb-8 lg:mb-12 text-sm text-primary font-semibold"
            >
                <template v-if="mounted">
                    <div class="w-2 h-2 bg-primary rounded-full" />
                    {{ displayedDeliveryTime }}
                </template>
            </section>

            <LazyProductDetailCampaignDeal
                v-if="product.extensions?.promotionDetails"
                class="mb-8 lg:mb-12"
            />

            <!-- is_configurable = is excentos product in general -->
            <div v-if="product?.customFields?.is_configurable" class="mb-6 flex flex-col gap-4">
                <div class="font-semibold">
                    {{ $t('product.detail.buyBox.configureClub') }}
                </div>

                <LazyProductDetailExcentos
                    v-if="product?.customFields?.is_configurable"
                    v-slot="{ initExcentos }"
                >
                    <button
                        type="button"
                        class="btn btn-primary mb-6 self-start lg:min-w-[260px]"
                        data-cy="show-excentos-configurator"
                        @click="initExcentos()"
                    >
                        {{ $t('product.detail.buyBox.startExcentos') }}
                    </button>
                </LazyProductDetailExcentos>
            </div>

            <LazyProductDetailBuyBoxCourseframeCourseSearch
                v-if="product.extensions?.courseframe?.courseSelection"
                :form-id="productFormId"
            />

            <!-- OITM_ManBatchSerial = true -> show shopware variant selection in addition to the excentos configuration button  -->
            <section v-if="configurator != null && product.parentId != null && (!isBatchSerialManaged || !product?.customFields?.is_configurable)" class="relative flex flex-col gap-4">
                <div v-if="product?.customFields?.is_configurable && !isBatchSerialManaged" class="font-semibold">
                    {{ $t('product.detail.buyBox.defaultVariantLabel') }}
                </div>
                <div v-if="product.extensions?.courseframe?.courseframe" class="font-semibold flex items-center gap-2">
                    <span class="inline-flex items-center justify-center w-5 h-5 rounded-full border border-primary text-primary text-xs shrink-0">{{ courseframeFormatStep }}</span>
                    {{ $t('courseframe.formatStepLabel') }}
                </div>
                <ProductDetailVariants
                    :variants="configurator"
                    :product-id="product.id"
                    :parent-id="product.parentId"
                    :preselect-options="!!displayFromVariants"
                    @loading="variantLoading = $event"
                    @option-selected="allOptionsSelected = $event"
                />
            </section>

            <form
                v-if="isDynamicBuyableVoucher || product.customFields?.titleist_logoball || product.extensions?.courseframe?.courseframe"
                :id="productFormId"
                ref="productFormEl"
                class="mb-4"
            >
                <!-- Dynamic Voucher Input -->
                <div v-if="isDynamicBuyableVoucher">
                    <label for="voucherValue" class="form-control w-full">
                        <div class="label">
                            <span class="label-text">{{ $t('product.detail.buyBox.voucherValueLabel') }}</span>
                        </div>
                        <input
                            id="voucherValue"
                            v-model="voucherValue"
                            type="number"
                            :placeholder="$t('product.detail.buyBox.voucherValuePlaceholder')"
                            class="input input-bordered w-full number-no-spinner"
                            required
                            :min="buyableVoucher?.from"
                            :max="buyableVoucher?.to"
                            step=".01"
                        >
                    </label>
                </div>

                <!-- Titleist Logoball Inputs -->
                <LazyProductDetailBuyBoxTitelistXmas
                    v-if="product.customFields?.titleist_logoball"
                    v-model="customPayload"
                />

                <!-- Courseframe Personalization Input -->
                <LazyProductDetailBuyBoxCourseframePersonalization
                    v-if="product.extensions?.courseframe?.courseframe"
                    :step="courseframeFormatStep + 1"
                />
            </form>

            <div class="flex flex-col gap-6 relative mt-8">
                <div
                    v-if="promotionData?.multiDeal43 && promotionData?.multiDeal43Text"
                    v-html="promotionData.multiDeal43Text"
                />

                <!-- qty, wishlist and add-to-cart reflow together: mobile keeps qty + wishlist on one line -->
                <div class="flex flex-wrap items-center gap-x-2 gap-y-6">
                    <div class="flex gap-3 justify-start items-center order-1 md:w-full">
                        <label for="quantity">
                            <span class="label-text font-semibold">{{ $t('product.detail.buyBox.quantity') }}</span>
                        </label>

                        <ProductQtySelector
                            :qty="quantity"
                            :min="product.minPurchase ?? 1"
                            :max="product.calculatedMaxPurchase ?? 100"
                            :input-disabled="false"
                            @update:qty="quantity = $event"
                        />
                    </div>

                    <!-- Wishlist sits on the qty line on mobile (ml-auto), beside add-to-cart on desktop (order swap) -->
                    <div class="order-2 ml-auto md:order-3 md:ml-0">
                        <WishlistProductToggleButton
                            :on-buy-box="true"
                            :product="product"
                        />
                    </div>

                    <div
                        v-if="!isBatchSerialManaged || !product?.customFields?.is_configurable"
                        class="order-3 w-full md:order-2 md:w-auto md:flex-1"
                        :class="{ 'tooltip before:max-w-none': !allOptionsSelected }"
                        :data-tip="$t('product.detail.buyBox.selectOptionsTip')"
                    >
                        <button
                            type="button"
                            class="btn btn-primary btn-lg md:btn-md w-full text-sm"
                            :class="{'btn-icon-left' : !loading}"
                            :disabled="loading || !allOptionsSelected || !mounted || !product.active || isPriceZero"
                            data-cy="product-detail-buybox-add-to-cart"
                            @click="onClickAddToCart()"
                        >
                            <span v-if="loading" class="loading" />
                            <MiscIcon v-if="!loading" name="shopping-cart" />
                            <span v-if="!loading && allOptionsSelected">
                                {{ $t('product.detail.buyBox.addToCart') }}
                            </span>
                            <span v-if="!loading && !allOptionsSelected">
                                {{ $t('product.detail.buyBox.chooseOptions') }}
                            </span>
                        </button>
                    </div>
                </div>

                <div v-if="!product.active" data-cy="productSoldOut" class="alert alert-error">
                    <MiscIcon name="minus-circle" />
                    <span>{{ $t('product.detail.buyBox.notAvailable') }}</span>
                </div>

                <div v-if="product.customFields?.team_clothing" class="font-bold" data-cy="teamWearInfo">
                    <div class="text-base-content">{{ t('product.detail.teamwear1') }}</div>
                    <div class="text-sale-focus">{{ t('product.detail.teamwear2') }}</div>
                </div>

                <div v-if="product.isCloseout && product.stock < 10" class="py-3 mb-2 -mt-2 border-t border-b text-sm flex justify-center items-center gap-2">
                    <MiscIcon name="flame" class="text-transparent" />{{ $t('product.detail.lowStockWarning') }}<MiscIcon name="flame" class="text-transparent" />
                </div>

                <ProductDetailPromotionTeaser />

                <div class="flex flex-col">
                    <ClientOnly>
                        <template v-if="currentSalesChannelCountryId != null && uspsByShippingCountry[currentSalesChannelCountryId] != null">
                            <div
                                v-for="(usp, index) in uspsByShippingCountry[currentSalesChannelCountryId]"
                                :key="index"
                                class="flex items-center gap-4 -mt-px border px-3 py-5 text-xs font-semibold"
                            >
                                <MiscIcon
                                    v-if="iconsByUsp[index]"
                                    :name="iconsByUsp[index]"
                                    size="md"
                                    class="inline-flex"
                                />
                                <template v-if="usp.trim() !== ''">
                                    {{ $t(usp) }}
                                </template>
                                <!-- use fallback usp if it's undefined for this shipping country -->
                                <template v-else>
                                    {{ $t(uspsByShippingCountry?.fallback?.[index]!) }}
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <div
                                v-for="(usp, index) in uspsByShippingCountry.fallback"
                                :key="index"
                                class="flex items-center gap-4 -mt-px border px-3 py-5 text-xs font-semibold"
                            >
                                <MiscIcon
                                    v-if="iconsByUsp[index]"
                                    :name="iconsByUsp[index]"
                                    size="md"
                                    class="inline-flex"
                                />
                                {{ $t(usp) }}
                            </div>
                        </template>
                    </ClientOnly>
                    <div v-if="config.bestPriceCms != null && config.bestPriceCms.trim() !== ''" class="flex items-center gap-4 border px-3 py-5 -mt-px text-xs font-semibold">
                        <MiscIcon name="cloud-percent" size="md" />
                        <LazyProductDetailBestPriceModal v-slot="slotProps">
                            <button type="button" class="link text-left" @click.prevent="slotProps.openModal()">
                                {{ $t('product.detail.buyBox.uspBestPrice') }}
                            </button>
                        </LazyProductDetailBestPriceModal>
                    </div>
                    <div class="flex items-center gap-4 border px-3 py-5 -mt-px text-xs font-semibold">
                        <MiscIcon name="cloud-heart" size="md" />
                        <MiscLink :to="$t('product.detail.buyBox.uspSatisfactionLink')" class="underline">
                            {{ $t('product.detail.buyBox.uspSatisfaction') }}
                        </MiscLink>
                    </div>
                    <div class="flex items-center gap-4 border px-3 py-5 -mt-px text-xs font-semibold">
                        <MiscIcon name="cloud-tick" size="md" />
                        <span>{{ $t('product.detail.buyBox.uspAuthorized') }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { ApiClientError, type ApiError } from '@shopware/api-client'
import { formValidation } from '@/utils/formValidation'
import { removeSizekickScriptAndData } from "~/utils/handle-sizekick-product-data"

const { t } = useI18n()
const nuxtApp = useNuxtApp()
const sidenav = useSidenav()

const detailPromotionData = inject<Ref>('detailPromotionData')
const { computedPromotionData: promotionData } = useDetailPromotion(detailPromotionData)

const { handleSizekickProductData } = useSizekick()
const { product, configurator } = useProduct()
const { unitPrice, displayFromVariants } = useProductPriceCustom(product)
const { addToCart, quantity } = useAddToCart(product)
const { cart } = useCart()
const { showNotification } = useNotification()
const variantLoading = ref(false)
const addToCartLoading = ref(false)
const loading = computed(() => {
    return addToCartLoading.value || variantLoading.value
})

const isPriceZero = computed(() => {
    return unitPrice.value != null && unitPrice.value === 0
})

const isBatchSerialManaged = computed(() => product.value.customFields?.OITM_ManBatchSerial === 'C')

const allOptionsSelected = ref(true)
const config = useRuntimeConfig().public
const buyableVoucher = computed(() => {
    return product.value.extensions?.netiEasyCouponProduct?.value?.[0]
})
const isDynamicBuyableVoucher = computed(() => {
    return buyableVoucher?.value != null && buyableVoucher.value.from !== buyableVoucher.value.to
})
const voucherValue = ref<null | number>(null)
const productFormEl = ref()
const productFormId = useId()
const customPayload = ref<Partial<Schemas['AddToCartPayload']>>({})
const { validateForm } = formValidation()
const { payload: courseframePayload, reset: resetCourseframe, courseSelection: courseframeHasCourseSelection } = useCourseframe()

// Products without the golf-course search start at the variant selection, so the visible steps shift up.
const courseframeFormatStep = computed(() => courseframeHasCourseSelection.value ? 2 : 1)

// USP Bar
const { sessionContext } = useSessionContext()
const currentSalesChannelCountryId = computed(() => sessionContext.value?.shippingLocation?.country?.id)

const uspsByShippingCountry = ref<Record<string, string[]>>({
    '23f1e3a2eba64c5197e47e7e5e41e785': [ // CH
        'layout.topBar.benefit2Ch',
        'layout.topBar.benefit4'
    ],
    'd1f39aad6c554a3183d35ba7a339a963': [ // LI (same as CH)
        'layout.topBar.benefit2Ch',
        'layout.topBar.benefit4'
    ],
    'cf949571b0ec4c8badf1fe72e113551b': [
        'layout.topBar.benefit2Sk',
        'layout.topBar.benefit4'
    ],
    '56ff618e238148ceac926cee50bfacbf': [
        'layout.topBar.benefit2Dk',
        'layout.topBar.benefit4'
    ],
    '0496f34fc43f4734afe6de8f86cbad06': [
        'layout.topBar.benefit2Pl',
        'layout.topBar.benefit4'
    ],
    'e8cb959a1c014e00a4c348d5fb4f037d': [
        'layout.topBar.benefit2Cz',
        'layout.topBar.benefit4'
    ],
    '5dd54cdb31ce4a46a602a15db3cfd3c0': [
        'layout.topBar.benefit2Fi',
        'layout.topBar.benefit4'
    ],
    'fallback': [
        'layout.topBar.benefit2',
        'layout.topBar.benefit4'
    ]
})
const iconsByUsp = ref([
    'package',
    'arrow-left',
])
const mounted = ref(false)

// Shared with the product structured data so both state the same delivery time.
const {
    isVisible: shouldShowDeliveryTime,
    displayText: displayedDeliveryTime,
} = useProductDeliveryTime()

onMounted(() => {
    mounted.value = true

    useRuntimeHook('productVariantChanged', () => {
        if (checkIfProductIsRelevantForSizekick(configurator)) {
            handleSizekickProductData({ product: product.value, configurator: configurator.value })
        }
    })

    if (checkIfProductIsRelevantForSizekick(configurator)) {
        handleSizekickProductData({ product: product.value, configurator: configurator.value })
    }
})

function checkIfProductIsRelevantForSizekick (configurator: UseProductReturn['configurator']) : boolean {
    return configurator.value?.some((variantGroup) => variantGroup.translated?.customFields?.custom_relevant_for_sizekick_toggle) || false
}

// The courseframe backend validation (e.g. "golf course required") answers 422 with a bare
// error array instead of the standard Shopware `{ errors: [...] }` envelope, so both shapes
// have to be checked here.
function extractApiErrorDetail (e: unknown): string | undefined {
    if (!(e instanceof ApiClientError)) return undefined

    const details = e.details as unknown
    const errors = Array.isArray(details) ? details as ApiError[] : (details as { errors?: ApiError[] })?.errors
    return errors?.[0]?.detail
}

async function onClickAddToCart () {
    let payload: Schemas['AddToCartPayload'] = {}

    // Validate entire form (voucher + titleist + courseframe + future inputs)
    if (isDynamicBuyableVoucher.value || product.value.customFields?.titleist_logoball || product.value.extensions?.courseframe?.courseframe) {
        const isValid = await validateForm(productFormEl.value)
        if (!isValid) {
            return
        }
    }

    // Add voucher data if applicable
    if (isDynamicBuyableVoucher.value) {
        payload = Object.assign(payload, {
            netiNextEasyCoupon: {
                voucherValue: voucherValue.value
            }
        })
    }

    // Merge any custom payload (logoball, future features, etc.)
    if (Object.keys(customPayload.value).length > 0) {
        payload = Object.assign(payload, customPayload.value)
    }

    // Merge courseframe golf-course selection and personalization
    if (product.value.extensions?.courseframe?.courseframe && Object.keys(courseframePayload.value).length > 0) {
        payload = Object.assign(payload, courseframePayload.value)
    }

    // Throw notification if qty is negative
    // OR qty is larger than maxPurchase (if isset)
    // OR product is in closeout AND qty is larger than stock
    if (
        quantity.value <= 0 ||
        (product?.value?.calculatedMaxPurchase != null && quantity.value > product?.value?.calculatedMaxPurchase) ||
        (product?.value?.isCloseout && quantity.value > product?.value?.stock)
    ) {
        let maxQty
        if (product?.value?.calculatedMaxPurchase != null && quantity.value > product?.value?.calculatedMaxPurchase) {
            maxQty = product?.value?.calculatedMaxPurchase
        } else if (product?.value?.isCloseout && quantity.value > product?.value?.stock) {
            maxQty = product?.value?.stock
        }
        quantity.value = maxQty != null ? maxQty : 1

        if (quantity.value > 0) {
            showNotification(
                t('product.detail.buyBox.addToCartInvalidQty', {
                    productName: product?.value?.name,
                    qty: maxQty
                }),
                'error'
            )
        }
        return
    }

    addToCartLoading.value = true
    try {
        if (Object.keys(payload).length > 0) {
            await addToCart(payload)
        } else {
            await addToCart()
        }
    } catch (e: unknown) {
        showNotification(extractApiErrorDetail(e) ?? (e instanceof Error ? e.message : String(e)), 'error', true)
        return
    } finally {
        addToCartLoading.value = false
    }

    if (cart.value?.errors) {
        const errorList = Array.isArray(cart.value.errors)
            ? cart.value.errors?.filter((error) => error.level !== 0)
            : Object.values(cart.value.errors).filter((error) => error.level !== 0)
        if (errorList.length > 0) {
            errorList.forEach(error => showNotification(error.message, 'error', true))
            return
        }
    }

    if (product.value.extensions?.courseframe?.courseframe) {
        resetCourseframe()
    }

    sidenav.open('cart', { triggerContext: 'addToCart' })
    nuxtApp.callHook('addToCart', { addQty: quantity.value, product: product.value, action: 'pdp' })
}

function showReviews () {
    nuxtApp.callHook('productReviewsShow')
}

function round (value: number, precision: number) {
    const multiplier = Math.pow(10, precision || 0)
    return Math.round(value * multiplier) / multiplier
}

onBeforeUnmount(() => {
    if (checkIfProductIsRelevantForSizekick(configurator)) {
        removeSizekickScriptAndData()
    }
})
</script>
